//
// # Route Width
//
// Single source of truth for per-segment route width semantics. Width is stored
// on `RoutePoint.width` (nm) and applies to the segment LEAVING that point
// (outgoing-segment convention), overriding the layer's default
// `MetalLayer.width`.
//
// Convention:
//   - Only the point that starts a WIRE segment carries a meaningful width.
//   - The last point of a path has no outgoing segment (its slot is ignored).
//   - A via segment (two points at the same location on different layers) takes
//     its geometry from the ViaDefinition, so the via point's own slot is
//     ignored; via arraying reads the two flanking wire widths, not the slot.
//
// The stored value is tri-state:
//   - a positive number  -> custom width
//   - null               -> explicitly cleared back to the layer default
//                           (null, not undefined, because a CRDT map update
//                            can only clear a field by overwriting it, never by
//                            deleting the key)
//   - undefined          -> never set; layer default
//
// null and undefined are treated identically as "use the layer default". Every
// consumer (rendering, draw mode, dialog, extraction) MUST go through these
// helpers rather than re-deriving the fallback, so the tri-state is handled in
// exactly one place.
//
// Encoding width on the point polyline keeps `Route` unchanged. Making segments
// (and vias) first-class in the schema — a list of wire/via segment objects
// each carrying width — is a cleaner but much larger future follow-up.
//

import * as schema from "GenAlphaSchema";

/** A route width as stored on a Route: nm number, or null/undefined for "layer default". */
export type RouteWidth = number | null | undefined;

// Payload for opening the route-width dialog. Lives here (with the rest of the
// route-width domain) rather than on the Platform interface, since it is shared
// by every layer that passes it: EditorCore (draw mode, context menu), the
// Platform notification, and the IDE dialog state. All widths are nanometers.
export interface EditRouteWidthParams {
  readonly currentWidth: number;
  readonly minWidth: number;
  readonly maxWidth: number;
  readonly defaultWidth: number;
  // Manufacturing grid (nm) the dialog snaps/validates entered widths against.
  readonly manufacturingGridNm: number;
  // Present when editing an existing route segment; absent when setting the
  // draw width. `segmentIndex` is the path index of the clicked segment (the
  // point whose outgoing-segment width is being edited).
  readonly routeId?: number;
  readonly segmentIndex?: number;
}

/**
 * Permissive fallback for a layer's max width when the PDK stack does not
 * specify `maxWidth`. Real per-layer values come from the stack JSON; until
 * those are populated this keeps the width editable without an artificial cap
 * that is obviously wrong.
 */
const FALLBACK_MAX_WIDTH_MULTIPLIER = 10;

/** Whether a stored route width means "use the layer default" (no custom width). */
export function isDefaultRouteWidth(width: RouteWidth): boolean {
  // Single predicate for both null and undefined — do not inline `=== null`
  // or `=== undefined` at call sites; they diverge and cause bugs.
  return width === null || width === undefined;
}

/** Effective render/extraction width (nm) for a route on the given layer. */
export function effectiveRouteWidth(
  width: RouteWidth,
  layer: Pick<schema.metal.MetalLayer, "width">
): number {
  // An explicit positive width wins; null, undefined, AND non-positive (an
  // invalid, degenerate width) all fall back to the layer default. Mirrors
  // Rust `RoutePoint::effective_width`, which floors non-positive widths too.
  return typeof width === "number" && width > 0 ? width : layer.width;
}

/** Resolved width bounds (nm) for a layer, with fallbacks when the PDK omits them. */
export interface RouteWidthConstraints {
  readonly min: number;
  readonly max: number;
  readonly default: number;
}

export function routeWidthConstraints(
  layer: Pick<schema.metal.MetalLayer, "width" | "minWidth" | "maxWidth">
): RouteWidthConstraints {
  const def = layer.width;
  return {
    min: layer.minWidth ?? def,
    max: layer.maxWidth ?? def * FALLBACK_MAX_WIDTH_MULTIPLIER,
    default: def,
  };
}

/**
 * Normalize a candidate width (nm) into the value to store on a route:
 * snapped to the manufacturing grid, clamped to the layer's [min, max], and
 * collapsed to null when it equals the layer default (so default-width routes
 * omit the field and stay byte-identical on save).
 *
 * Snapping first makes the default-collapse an exact integer comparison, so a
 * route can always return to "use default" via +/- stepping.
 */
export function normalizeRouteWidth(
  candidate: number,
  layer: Pick<schema.metal.MetalLayer, "width" | "minWidth" | "maxWidth">,
  manufacturingGridNm: number
): number | null {
  return normalizeWidthToConstraints(
    candidate,
    routeWidthConstraints(layer),
    manufacturingGridNm
  );
}

/**
 * Same as normalizeRouteWidth but against already-resolved constraints. Used by
 * the width dialog, which is handed the bounds rather than the layer. Keeps the
 * snap/clamp/default-collapse logic in exactly one place.
 */
export function normalizeWidthToConstraints(
  candidate: number,
  constraints: RouteWidthConstraints,
  manufacturingGridNm: number
): number | null {
  const { min, max, default: def } = constraints;
  const grid = manufacturingGridNm > 0 ? manufacturingGridNm : 1;
  // Snap the bounds INWARD to the grid (min up, max down) so clamping a snapped
  // candidate can't land off-grid when the PDK's min/max aren't grid-aligned.
  const lo = Math.ceil(min / grid) * grid;
  const hi = Math.floor(max / grid) * grid;
  // Pathological: no grid-aligned width fits in [min, max] — fall back to the
  // layer default rather than emit an off-grid or out-of-range value.
  if (lo > hi) return null;
  const snapped = Math.round(candidate / grid) * grid;
  const clamped = Math.max(lo, Math.min(hi, snapped));
  return clamped === def ? null : clamped;
}
